import React, { useEffect, useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import {
  type ReaderAntennas,
  fetchScanDetail,
  readerGroups,
} from "../../services/boxDetailApi";

/**
 * AntennaStrip — the per-antenna EPC map, shrunk to fit a map popup.
 *
 * One square per antenna, no numbers: red = won zero tags on the last scan,
 * filled = won some. The pattern is the signal — a box scanning 400 tags on 15
 * of 16 antennas and zero on the 16th has a cable problem you can see at a
 * glance, which is why this lives next to the connectivity dots rather than a
 * page away.
 *
 * "WON", not "saw", and the difference matters. The scan stores one row per
 * tag carrying its BEST-RSSI antenna, so an antenna that reads fine but always
 * loses to its neighbour also shows red. A red square means look, not broken.
 * Upstream (`gen5_edge_processor_v2` branch `heat-map`) is adding a per-tag
 * 32-bit antenna mask that will make this an honest "saw".
 *
 * Counts ARE in each square's tooltip; the popup is too tight for them to be
 * legible inline, and the detail page's grid (SignalDetailPage, "Last scan")
 * is the place that shows them.
 *
 * Loaded ON DEMAND, like every other per-antenna read: the counts are NOT in
 * the box snapshot (CLAUDE.md §7 — they'd cost a per-box query every cycle for
 * all 75 boxes), so this fires one small request when a popup opens. React
 * mounts popup children only when the popup is opened, so browsing the map
 * costs nothing until someone clicks a pin.
 *
 * One honest limit, inherited from `fetch_box_scan_detail`: the server fills
 * antennas 1..max(seen), so an antenna that read zero shows red only when a
 * HIGHER-numbered antenna read something. A dead antenna at the top of the
 * range is simply absent — count the squares against what the box should have.
 *
 * Renders NOTHING (no spinner, no error, no empty box) unless there is a real
 * antenna list: the endpoint returns antennas for platform users only, and a
 * popup that reflows after its own fetch resolves is worse than one that just
 * gains a strip.
 */
export const AntennaStrip: React.FC<{ scu: string; className?: string }> = ({
  scu,
  className,
}) => {
  const { token } = useAuth();
  const [readers, setReaders] = useState<ReaderAntennas[]>([]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    fetchScanDetail(scu, token)
      .then((d) => {
        if (alive) setReaders(readerGroups(d));
      })
      .catch(() => {
        // Non-platform callers get no antenna block, and a popup is not the
        // place to report that a nice-to-have failed. Stay silent.
      });
    return () => {
      alive = false;
    };
  }, [scu, token]);

  if (readers.length === 0) return null;

  const total = readers.reduce((n, r) => n + r.antennas.length, 0);
  const dead = readers.reduce(
    (n, r) => n + r.antennas.filter((a) => a.epc_count === 0).length,
    0,
  );
  const multi = readers.length > 1;

  return (
    <div
      className={cn("shrink-0", className)}
      title={
        `EPCs per antenna, last scan — ${total} antenna${total === 1 ? "" : "s"}` +
        (multi ? ` across ${readers.length} readers` : "") +
        (dead > 0 ? `, ${dead} winning zero tags` : ", all reading") +
        ". Counts are best-RSSI wins, not every read — a zero is worth a look," +
        " not proof of a dead antenna. Hover a square for its count."
      }
    >
      <div className="text-[9px] uppercase tracking-wide text-secondary-400 text-right leading-none mb-0.5">
        Ant
      </div>
      {/* One block per reader, STACKED — port numbers are per reader, so two
          readers' maps are two maps, not one longer one. The reader label only
          appears when there is more than one, so the single-reader fleet of
          today looks exactly as it did. */}
      <div className="flex flex-col gap-1 items-end">
        {readers.map((r) => (
          <div key={r.reader_id}>
            {multi && (
              <div className="text-[8px] text-secondary-400 text-right leading-none mb-[1px]">
                R{r.reader_id}
              </div>
            )}
            {/* Fixed 8-wide, so the block stays the same width box to box and
                only grows downward — a 32-port box (a 20 ft box) would be an
                8-row tower at 4-wide, taller than the dot column beside it.
                8 x 7px + gaps ~ 70px, which fits the 300px popup. */}
            <div className="grid grid-cols-8 gap-[2px] justify-items-end">
              {r.antennas.map((a) => (
                <span
                  key={a.antenna_number}
                  title={`${multi ? `Reader ${r.reader_id} ` : ""}antenna ${
                    a.antenna_number
                  }: best RSSI on ${a.epc_count} tag${
                    a.epc_count === 1 ? "" : "s"
                  }`}
                  className={cn(
                    "block w-[7px] h-[7px] rounded-[1px] border",
                    a.epc_count === 0
                      ? "bg-error-400 border-error-500"
                      : "bg-success-400 border-success-500",
                  )}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AntennaStrip;
